import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { Link, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import NavBar from "../navbar";
import { validateLoggedIn } from "../utils";
import {
    STATUSES,
    STATUS_LABELS,
    downloadAttachment,
    fetchTicket,
    formatDate,
    formatSize,
    replyToTicket,
    updateTicketStatus,
} from "./support_api";

const MIN_BODY = 10;
const MAX_BODY = 10000;
const MAX_FILES = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf"];
const ACCEPT = ACCEPTED_TYPES.join(",");

const Attachments = ({ attachments, onError }) => {
    if (!attachments || attachments.length === 0) return null;
    return (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
            {attachments.map((attachment) => (
                <Chip
                    key={attachment.id}
                    size="small"
                    variant="outlined"
                    label={`${attachment.file_name} · ${formatSize(attachment.size)}`}
                    onClick={() => downloadAttachment(attachment).catch((err) => onError(err.message))}
                />
            ))}
        </Stack>
    );
};

const Message = ({ message, onError }) => {
    const fromSupport = message.author === "support";
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                alignSelf: fromSupport ? "flex-end" : "flex-start",
                maxWidth: "85%",
                minWidth: 320,
                bgcolor: fromSupport ? "#EEF4FF" : "background.paper",
            }}
        >
            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    {fromSupport ? "Support" : "User"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {formatDate(message.created_at)}
                </Typography>
            </Stack>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {message.body}
            </Typography>
            <Attachments attachments={message.attachments} onError={onError} />
        </Paper>
    );
};

const SupportTicket = () => {
    const { ticket_id } = useParams();
    const [ticket, setTicket] = useState(null);
    const [error, setError] = useState(null);
    const [body, setBody] = useState("");
    const [files, setFiles] = useState([]);
    const [sending, setSending] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    const fileInput = useRef(null);

    const load = useCallback(async () => {
        try {
            setTicket(await fetchTicket(ticket_id));
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        }
    }, [ticket_id]);

    useEffect(() => {
        const validate = validateLoggedIn();
        if (validate) {
            setError("You are not logged in.");
            return;
        }
        load();
    }, [load]);

    const pickFiles = (event) => {
        const incoming = Array.from(event.target.files || []);
        event.target.value = "";
        if (files.length + incoming.length > MAX_FILES) {
            setError(`You can attach up to ${MAX_FILES} files`);
            return;
        }
        const rejected = incoming.find((file) => (file.type !== "" && !ACCEPTED_TYPES.includes(file.type)) || file.size > MAX_FILE_BYTES);
        if (rejected) {
            setError(`${rejected.name}: only images and PDF up to 10 MB are accepted`);
            return;
        }
        setError(null);
        setFiles([...files, ...incoming]);
    };

    const bodyLength = body.trim().length;
    const bodyTooLong = bodyLength > MAX_BODY;
    const canSend = !sending && bodyLength >= MIN_BODY && !bodyTooLong;

    const send = async () => {
        const trimmed = body.trim();
        if (!canSend) return;
        setSending(true);
        setError(null);
        try {
            await replyToTicket(ticket_id, trimmed, files);
            setBody("");
            setFiles([]);
            await load();
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setSending(false);
        }
    };

    const changeStatus = async (status) => {
        setStatusSaving(true);
        setError(null);
        try {
            await updateTicketStatus(ticket_id, status);
            await load();
        } catch (err) {
            Sentry.captureException(err);
            setError(err.message);
        } finally {
            setStatusSaving(false);
        }
    };

    if (!ticket && !error) {
        return (
            <>
                <NavBar />
                <Box textAlign="center" p={2}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    return (
        <>
            <NavBar />
            <Box sx={{ p: 3, maxWidth: 1100, margin: "auto" }}>
                <Button component={Link} to="/support-tickets" size="small" sx={{ mb: 2 }}>
                    ← All tickets
                </Button>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                {ticket && (
                    <>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
                                {ticket.subject}
                            </Typography>
                            <Stack direction="row" spacing={3} sx={{ mt: 2, flexWrap: "wrap" }} useFlexGap>
                                <Typography variant="body2">
                                    <strong>User:</strong>{" "}
                                    <Link to={`/users/${ticket.user_id}/edit`}>{ticket.user_email}</Link> ({ticket.user_name})
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Type:</strong> {ticket.type}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Category:</strong> {ticket.category || "—"}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Source:</strong> {ticket.source}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Created:</strong> {formatDate(ticket.created_at)}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Last activity:</strong> {formatDate(ticket.last_message_at)}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                <TextField
                                    select
                                    size="small"
                                    label="Status"
                                    value={ticket.status}
                                    onChange={(event) => changeStatus(event.target.value)}
                                    disabled={statusSaving}
                                    sx={{ minWidth: 220 }}
                                >
                                    {STATUSES.map((value) => (
                                        <MenuItem key={value} value={value}>
                                            {STATUS_LABELS[value]}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                {ticket.status !== "closed" && (
                                    <Button variant="outlined" color="error" disabled={statusSaving} onClick={() => changeStatus("closed")}>
                                        Close ticket
                                    </Button>
                                )}
                            </Stack>
                        </Paper>

                        <Stack spacing={2} sx={{ mb: 3 }}>
                            {ticket.messages.map((message) => (
                                <Message key={message.id} message={message} onError={setError} />
                            ))}
                        </Stack>

                        <Divider sx={{ mb: 3 }} />

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                Reply to user
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                The reply appears in the user&apos;s dashboard and is sent to {ticket.user_email} as an email copy.
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                label="Reply"
                                value={body}
                                onChange={(event) => setBody(event.target.value)}
                                disabled={sending}
                                error={bodyTooLong}
                                helperText={
                                    bodyLength > 0 && bodyLength < MIN_BODY
                                        ? `At least ${MIN_BODY} characters`
                                        : bodyTooLong
                                            ? `At most ${MAX_BODY} characters`
                                            : " "
                                }
                            />
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }} useFlexGap>
                                <Button variant="outlined" size="small" onClick={() => fileInput.current && fileInput.current.click()} disabled={sending || files.length >= MAX_FILES}>
                                    Attach files
                                </Button>
                                <Typography variant="caption" color="text.secondary">
                                    Up to {MAX_FILES} files, 10 MB each. Images and PDF only.
                                </Typography>
                                <input ref={fileInput} type="file" hidden multiple accept={ACCEPT} onChange={pickFiles} />
                                {files.map((file, index) => (
                                    <Chip
                                        key={`${file.name}-${index}`}
                                        size="small"
                                        label={`${file.name} · ${formatSize(file.size)}`}
                                        onDelete={() => setFiles(files.filter((_, i) => i !== index))}
                                    />
                                ))}
                            </Stack>
                            <Button variant="contained" onClick={send} disabled={!canSend}>
                                {sending ? "Sending..." : "Send reply"}
                            </Button>
                        </Paper>
                    </>
                )}
            </Box>
        </>
    );
};

export default SupportTicket;
